import { HttpError } from './errors';

/**
 * Creates a response handler that throws HttpError on non-2xx responses.
 * This is placed INSIDE the middleware chain so middleware (tokenRefresh, etc.)
 * can catch and handle HttpErrors.
 */
function createResponseHandler(fetchFn) {
  return async (url, init) => {
    // Strip non-standard internal flags before passing to native fetch
    const { __rawUpload: _rawUpload, ...fetchInit } = init;
    const response = await fetchFn(url, fetchInit);

    if (!response.ok) {
      let data = {};
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        }
      } catch {
        // ignore JSON parse errors
      }
      throw new HttpError(response.status, response.statusText, data, url);
    }

    return response;
  };
}

export function createHttpClient({
  baseURL = '',
  headers: defaultHeaders = {},
  middleware = []
} = {}) {
  // Inner: fetch → responseHandler (throws HttpError on non-2xx)
  // Outer: middleware chain wraps responseHandler so they can catch HttpErrors
  let enhancedFetch = createResponseHandler(globalThis.fetch.bind(globalThis));

  for (let idx = middleware.length - 1; idx >= 0; idx--) {
    enhancedFetch = middleware[idx](enhancedFetch);
  }

  async function request(method, path, body, options = {}) {
    // URL resolution: if path is absolute URL, use it directly (e.g. S3 presigned URLs)
    // Otherwise, string concatenation (same as axios baseURL + url)
    let url;
    try {
      url = new URL(path);
      // path is already a full URL — use as-is
    } catch {
      const base = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
      const normalizedPath = path.startsWith('/') ? path : '/' + path;
      url = new URL(base + normalizedPath);
    }

    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        url.searchParams.set(key, String(value));
      });
    }

    const mergedHeaders = { ...defaultHeaders, ...options.headers };

    // Forward custom properties (e.g. skipSoulodyTokenEnsure) to init for middleware access
    const { params: _p, headers: _h, signal: _s, raw: _r, ...extraOptions } = options;

    const init = {
      ...extraOptions,
      method,
      headers: mergedHeaders
    };

    // Mark raw uploads (e.g. S3 presigned) so middleware can skip auth headers
    if (options.raw) {
      init.__rawUpload = true;
    }

    if (body !== undefined && body !== null) {
      if (options.raw) {
        init.body = body;
      } else {
        init.body = JSON.stringify(body);
        if (!mergedHeaders['Content-Type']) {
          mergedHeaders['Content-Type'] = 'application/json';
        }
      }
    }

    if (options.signal) {
      init.signal = options.signal;
    }

    // Middleware chain already handles error throwing (responseHandler inside chain)
    const response = await enhancedFetch(url.toString(), init);

    // At this point, response is guaranteed 2xx
    if (response.status === 204) {
      return null;
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    return response.text();
  }

  return {
    get: (path, options) => request('GET', path, undefined, options),
    post: (path, body, options) => request('POST', path, body, options),
    put: (path, body, options) => request('PUT', path, body, options),
    patch: (path, body, options) => request('PATCH', path, body, options),
    delete: (path, options) => request('DELETE', path, undefined, options)
  };
}
